/* import __COLOCATED_TEMPLATE__ from './workspace-command-k.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type Router from '@ember/routing/router-service';
import type Session from 'embercom/services/session';
import { action } from '@ember/object';
import type InboxState from 'embercom/services/inbox-state';
import { ConversationsViewType } from 'embercom/services/inbox-state';
import { InboxCategory } from 'embercom/models/data/inbox/inbox-categories';
import { type ValidLocales } from './user-menu/locale-switcher';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type ExperimentsApi from 'embercom/services/experiments-api';
import { type CommandKMetadata } from 'embercom/services/command-k';
import { type JumpToValue } from 'embercom/objects/inbox/command-k/jump-to';
import type IntercomCallService from 'embercom/services/intercom-call-service';
import type Theme from 'embercom/objects/inbox/types/theme';
import type SearchStateService from 'embercom/services/search-state-service';
import type ThemeService from 'embercom/services/theme-service';
import type PermissionsModal from 'embercom/services/permissions-modal';
import type IntlService from 'embercom/services/intl';

export default class WorkspaceCommandK extends Component {
  @service declare router: Router;
  @service declare session: Session;
  @service declare inboxState: InboxState;
  @service declare experimentsApi: ExperimentsApi;
  @service declare intercomCallService: IntercomCallService;
  @service declare searchStateService: SearchStateService;
  @service declare themeService: ThemeService;
  @service declare permissionsModal: PermissionsModal;
  @service declare intl: IntlService;

  private get canCreateConversations(): boolean {
    return this.session.teammate.permissions.canManageConversationCreation;
  }

  private checkConversationCreationPermission(): boolean {
    if (!this.canCreateConversations) {
      this.permissionsModal.showModal({
        permissionsLabel: this.intl.t(
          'inbox.conversation-header.create-conversation-permission-label',
        ),
      });
      return false;
    }
    return true;
  }

  get isOnSearchRoute() {
    return (
      this.router.currentRouteName?.startsWith('inbox.workspace.inbox.search') ||
      this.router.currentRouteName?.startsWith('inbox.workspace.search')
    );
  }

  get shouldDisplayWorkspaceActions() {
    return (
      !this.inboxState.hasSelectedConversations &&
      !this.router.currentRouteName?.startsWith('inbox.workspace.inbox.linked-conversations')
    );
  }

  trackingDataFromSelectValue(locale: string) {
    return {
      language: locale,
    };
  }

  get currentAdminCanManageTags() {
    return this.session.teammate.permissions.canManageTags;
  }

  @action manageTags() {
    let url: string = this.router.urlFor('apps.app.settings.data.tags', this.session.workspace);
    safeWindowOpen(url);
  }

  @action globalSearch() {
    this.searchStateService.toggleGlobalSearch();
  }

  @action manageMacros() {
    let manageMacrosRoute = 'apps.app.settings.helpdesk.macros';
    let url: string = this.router.urlFor(manageMacrosRoute, this.session.workspace);
    safeWindowOpen(url);
  }

  @action manageAssignmentRules() {
    let manageAssignmentRulesRoute = 'apps.app.settings.helpdesk.assignments';
    let url: string = this.router.urlFor(manageAssignmentRulesRoute, this.session.workspace);
    safeWindowOpen(url);
  }

  @action manageWorkflowConnectorActions() {
    let actionsRoute = 'apps.app.settings.app-settings.custom-actions';
    let url: string = this.router.urlFor(actionsRoute, this.session.workspace);
    safeWindowOpen(url);
  }

  @action goToAppStore() {
    this.router.transitionTo('apps.app.appstore.index', this.session.workspace.id);
  }

  @action jumpToInbox(inbox: any) {
    if (inbox.category === InboxCategory.Dashboard) {
      this.router.transitionTo('inbox.workspace.dashboard');
    } else {
      this.router.transitionTo('inbox.workspace.inbox.inbox', inbox.category, inbox.id);
    }
  }

  @action commandKGoToTrackingDataFromSelectValue(goto: JumpToValue) {
    let inboxType;

    switch (goto.category) {
      case InboxCategory.Shared:
        inboxType = goto.id;
        break;
      case InboxCategory.Admin:
        if (goto.id.toString() === this.session.teammate.id.toString()) {
          inboxType = 'me';
        } else {
          inboxType = goto.category;
        }
        break;
      default:
        inboxType = goto.category;
        break;
    }

    return {
      selected_inbox: inboxType,
      selected_inbox_id: goto.id,
      view_type: goto.category === InboxCategory.View ? goto.viewSummary.systemType : undefined,
      has_dynamic_admin_assignee_predicates:
        goto.category === InboxCategory.View
          ? goto.viewSummary.hasDynamicAdminAssigneePredicates
          : undefined,
    };
  }

  @action
  goToNewConversation() {
    if (!this.checkConversationCreationPermission()) {
      return;
    }

    this.inboxState.goBackToInboxList();
    this.router.transitionTo('inbox.workspace.inbox.new-conversation', {
      queryParams: {
        ticketTypeId: undefined,
      },
    });
  }

  @action
  startOutboundCall() {
    if (!this.checkConversationCreationPermission()) {
      return;
    }

    this.inboxState.goBackToInboxList();
    this.intercomCallService.openDialler({
      diallerContext: 'outbound',
      analyticsConfig: {
        place: 'cmd_k',
      },
    });
  }

  @action async changeLanguage(value: ValidLocales) {
    this.session.changeLanguage(value);
  }

  @action switchToTableView(_: any, __: any, metadata: CommandKMetadata) {
    this.inboxState.switchConversationsView(ConversationsViewType.TableFullscreen, {
      keyboardShortcutUsed: metadata.shortcutUsed,
      section: metadata.shortcutUsed ? undefined : 'command_k',
    });
  }

  @action transitionToSearch() {
    this.router.transitionTo('inbox.workspace.inbox.search');
  }

  @action setTheme(theme: 'dark' | 'light' | 'system') {
    this.themeService.setTheme(theme as Theme);
  }

  @action handleCreateMacro() {
    let hasPermission = this.inboxState.handleCreateMacroAction('inbox_command_k');
    if (hasPermission) {
      this.inboxState.toggleCreateMacroModal();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::WorkspaceCommandK': typeof WorkspaceCommandK;
    'inbox2/workspace-command-k': typeof WorkspaceCommandK;
  }
}
